/**
 * The user's AI preferences, read SYNCHRONOUSLY from the store.
 *
 * This is the only way a service should learn its starting jurisdiction or
 * output language. The values are hydrated once by the platform bootstrap
 * (`hydrateUserPreferences`, dispatched from `PrivateLayout`) and persisted
 * with the `user` slice, so on mount a screen already has them and can
 * preselect on its FIRST render - no per-component fetch, no empty control
 * that pops in a value a beat later.
 *
 * Before this existed, each selector fetched the preference itself (matter
 * creation and contract review piggybacked on `/matters/dropdowns`, research
 * read a store section nothing guaranteed was populated). That is why a user
 * with Bulgaria + English saved in Settings still saw "Not set" in research:
 * the store section was empty and only the Settings page ever refilled it.
 *
 * Reading rules are the shared ones (`utils/aiPreferences`): jurisdiction is
 * `CountryOfJurisdiction || jurisdiction` with a legacy `US` treated as unset,
 * and unset is a fully valid answer - never a reason to block.
 */

import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { readPreferredJurisdiction, readPreferredLanguage } from '../utils/aiPreferences';

export interface AIPreferences {
  /** ISO-ish jurisdiction code, `''` when unset. */
  jurisdiction: string;
  /** AI output language CODE, `''` when unset. */
  language: string;
  /** Speaker voice, `''` when unset. */
  voice: string;
  /**
   * The app-boot hydration has not answered yet.
   *
   * Only true during initial bootstrap - a selector shows a skeleton while it
   * holds, then either a preselected value or its placeholder. It never goes
   * true again on navigation, because the preference is already in the store.
   */
  isHydrating: boolean;
}

export const useAIPreferences = (): AIPreferences => {
  const aiSettings = useSelector(
    (state: RootState) => state.user?.userInfo?.preferences?.ai_settings,
  );
  const status = useSelector((state: RootState) => state.user?.preferencesStatus);

  return useMemo(() => {
    const jurisdiction = readPreferredJurisdiction(aiSettings);
    const language = readPreferredLanguage(aiSettings);
    const voice = String(aiSettings?.speaker_voice || '').trim();

    return {
      jurisdiction,
      language,
      voice,
      // A rehydrated store that already carries the section is not hydrating,
      // whatever the status says - the values are right there to render.
      isHydrating:
        !(aiSettings && Object.keys(aiSettings).length > 0) &&
        (status === 'idle' || status === 'loading' || status === undefined),
    };
  }, [aiSettings, status]);
};

export default useAIPreferences;
